import axiosClient from './axiosClient';

// Lessons
export const createLesson = (data: any) => {
    return axiosClient.post('/lessons', data);
};

export const updateLesson = (id: string, data: any) => {
    return axiosClient.put(`/lessons/${id}`, data);
};

export const uploadLessonDocument = (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return axiosClient.post(`/lessons/${id}/document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};

export const uploadLessonVideo = (id: string, title: string, file: File, onUploadProgress?: (progressEvent: any) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    return axiosClient.post(`/lessons/${id}/video-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress
    });
};

export const addLessonVideoUrl = (id: string, title: string, url: string) => {
    return axiosClient.post(`/lessons/${id}/video-url`, null, { params: { title, url } });
};

export const deleteLessonVideo = (lessonId: string, videoId: string) => {
    return axiosClient.delete(`/lessons/${lessonId}/videos/${videoId}`);
};

// Lesson Plans
export const getLessonPlans = (lessonId: string) => {
    return axiosClient.get(`/workspace/lessons/${lessonId}/plans`);
};

export const createLessonPlan = (data: any) => {
    return axiosClient.post('/workspace/plans', data);
};

export const generateLessonPlan = (lessonId: string, params?: { difficulty?: string; temperature?: number }) => {
    return axiosClient.post(`/workspace/lessons/${lessonId}/generate-plan`, null, { params });
};

export const generateSlides = (lessonId: string, params?: { temperature?: number }) => {
    return axiosClient.post(`/workspace/lessons/${lessonId}/generate-slides`, null, { params });
};

export const updateLessonPlan = (planId: string, data: any) => {
    return axiosClient.put(`/workspace/plans/${planId}`, data);
};

export const deleteLessonPlan = (planId: string) => {
    return axiosClient.delete(`/workspace/plans/${planId}`);
};

export const shareLessonPlan = (planId: string, data: { student_id?: string, class_id?: string }) => {
    return axiosClient.post(`/workspace/plans/${planId}/share`, data);
};

export const getLessonPlanAccess = (planId: string) => {
    return axiosClient.get(`/workspace/plans/${planId}/access`);
};

export const revokeLessonPlanAccess = (accessId: string) => {
    return axiosClient.delete(`/workspace/plans/access/${accessId}`);
};

// Flashcards
export const getFlashcardSets = (lessonId: string) => {
    return axiosClient.get(`/workspace/lessons/${lessonId}/flashcards`);
};

export const createFlashcardSet = (data: any) => {
    return axiosClient.post('/workspace/flashcards', data);
};

export const generateFlashcards = (lessonId: string, params?: { difficulty?: string; temperature?: number }) => {
    return axiosClient.post(`/workspace/lessons/${lessonId}/generate-flashcards`, null, { params });
};

export const updateFlashcardSet = (setId: string, data: any) => {
    return axiosClient.put(`/workspace/flashcard-sets/${setId}`, data);
};

export const deleteFlashcardSet = (setId: string) => {
    return axiosClient.delete(`/workspace/flashcard-sets/${setId}`);
};

// Access
export const shareLesson = (lessonId: string, data: { student_ids?: string[], class_ids?: string[] }) => {
    return axiosClient.post(`/workspace/lessons/${lessonId}/share`, data);
};

export const shareFlashcardSet = (setId: string, data: { student_id?: string, class_id?: string }) => {
    return axiosClient.post(`/flashcards/${setId}/share`, data);
};

export const getFlashcardSetAccess = (setId: string) => {
    return axiosClient.get(`/flashcards/${setId}/access`);
};

export const revokeFlashcardSetAccess = (accessId: string) => {
    return axiosClient.delete(`/flashcards/access/${accessId}`);
};

// Single Card
export const addCardToSet = (setId: string, data: { front: string, back: string, image_url?: string, audio_url?: string }) => {
    return axiosClient.post(`/workspace/flashcard-sets/${setId}/cards`, data);
};

export const getCardsInSet = (setId: string) => {
    return axiosClient.get(`/workspace/flashcard-sets/${setId}/cards`);
};

export const updateFlashcard = (cardId: string, data: { front: string, back: string, image_url?: string, audio_url?: string }) => {
    return axiosClient.put(`/workspace/flashcards/${cardId}`, data);
};

export const deleteFlashcard = (cardId: string) => {
    return axiosClient.delete(`/workspace/flashcards/${cardId}`);
};

// Unsplash Image Search
export const searchUnsplash = (query: string) => {
    return axiosClient.get('/workspace/utils/unsplash-search', { params: { query } });
};

export const uploadFlashcardImage = (file: File, folder?: string, parentId?: string, parentClass?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (parentId) formData.append('parent_id', parentId);
    if (parentClass) formData.append('parent_class', parentClass);
    if (folder) formData.append('folder', folder);
    
    return axiosClient.post('/workspace/utils/upload-image', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
};

export const uploadFlashcardAudio = (file: File, folder: string = 'flashcards/audio', parentId?: string, parentClass?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (parentId) formData.append('parent_id', parentId);
    if (parentClass) formData.append('parent_class', parentClass);
    if (folder) formData.append('folder', folder);
    
    return axiosClient.post<{ url: string }>('/workspace/utils/upload-image', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
};

export const saveRemoteImage = (url: string, folder?: string) => {
    return axiosClient.post(`/workspace/utils/save-remote-image`, { url, folder });
};

export const deleteMedia = (url: string) => {
    return axiosClient.post(`/workspace/utils/delete-media`, { url });
};

// Lesson Documents
export const uploadLessonDocuments = (lessonId: string, files: File[], onUploadProgress?: (progressEvent: any) => void) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return axiosClient.post(`/lessons/${lessonId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress
    });
};

export const deleteLessonDocument = (lessonId: string, mediaId: string) => {
    return axiosClient.delete(`/lessons/${lessonId}/documents/${mediaId}`);
};

export const extractDocumentContent = (lessonId: string, mediaId: string, forceAi?: boolean) => {
    const url = `/lessons/${lessonId}/documents/${mediaId}/extract${forceAi ? '?force_ai=true' : ''}`;
    return axiosClient.post(url);
};
